import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../../../../api';
import { toast, ToastContainer } from 'react-toastify';

const ChecklistHistoryModal = ({ vehicleId, onViewDetails, refreshTrigger }) => {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [filterName, setFilterName] = useState('');

  /* --------------------------------------------------------------
     UTILS
     -------------------------------------------------------------- */
  const capitalizeName = (name) => {
    if (!name) return 'Unknown';
    return name
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };

  /* --------------------------------------------------------------
     FETCH HISTORY – runs on modal show + refreshTrigger
     -------------------------------------------------------------- */
  const fetchChecklistHistory = useCallback(async () => {
    const currentVehicleId = sessionStorage.getItem('currentVehicleId') || vehicleId;

    if (!currentVehicleId) {
      setHistory([]);
      setFilteredHistory([]);
      return;
    }

    setIsLoading(true);
    try {
      const authToken = sessionStorage.getItem('auth_token');
      if (!authToken) throw new Error('Authentication token missing');

      const response = await axiosInstance.get(
        `ems/get-vehicle-checklist-history/${currentVehicleId}`,
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const rawItems = response.data?.data ?? [];
      if (!Array.isArray(rawItems)) throw new Error('Invalid data format');

      // Group by submission_batch_id
      const grouped = rawItems.reduce((acc, item) => {
        const batchId = item.submission_batch_id;
        if (!acc[batchId]) {
          const first = item.user?.first_name || '';
          const last = item.user?.last_name || '';
          const full = first || last ? `${first} ${last}`.trim() : 'Unknown';
          acc[batchId] = {
            id: batchId,
            checked_date: item.submitted_at,
            checked_by: capitalizeName(full),
          };
        }
        return acc;
      }, {});

      const sorted = Object.values(grouped).sort(
        (a, b) => new Date(b.checked_date) - new Date(a.checked_date)
      );

      setHistory(sorted);
      applyFilters(sorted);
    } catch (err) {
      console.error('History fetch error:', err);
      toast.error(
        err.response?.data?.message || 'Failed to load checklist history'
      );
      setHistory([]);
      setFilteredHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [vehicleId]);

  /* --------------------------------------------------------------
     FILTER LOGIC
     -------------------------------------------------------------- */
  const applyFilters = (data) => {
    let filtered = [...data];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (filterType === 'today') {
      filtered = filtered.filter((item) => {
        const d = new Date(item.checked_date);
        d.setHours(0, 0, 0, 0);
        return d.getTime() === today.getTime();
      });
    } else if (filterType === 'date' && filterDate) {
      const sel = new Date(filterDate);
      sel.setHours(0, 0, 0, 0);
      filtered = filtered.filter((item) => {
        const d = new Date(item.checked_date);
        d.setHours(0, 0, 0, 0);
        return d.getTime() === sel.getTime();
      });
    } else if (filterType === 'name' && filterName) {
      filtered = filtered.filter((i) =>
        i.checked_by.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    filtered.sort((a, b) => new Date(b.checked_date) - new Date(a.checked_date));
    setFilteredHistory(filtered);
  };

  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
    setFilterDate('');
    setFilterName('');
  };

  const handleSearch = () => applyFilters(history);

  const handleViewDetails = (submissionBatchId) => {
    sessionStorage.setItem('currentSubmissionId', submissionBatchId);
    onViewDetails?.(submissionBatchId);
  };

  /* --------------------------------------------------------------
     EFFECTS: Auto-fetch on modal show + refreshTrigger
     -------------------------------------------------------------- */
  // 1. Fetch when modal opens
  useEffect(() => {
    const modalEl = document.getElementById('bs-checklist-history-list');
    if (!modalEl) return;

    const onShow = () => fetchChecklistHistory();
    modalEl.addEventListener('show.bs.modal', onShow);

    return () => modalEl.removeEventListener('show.bs.modal', onShow);
  }, [fetchChecklistHistory]);

  // 2. Fetch when refreshTrigger changes (external update)
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchChecklistHistory();
    }
  }, [refreshTrigger, fetchChecklistHistory]);

  // 3. Optional: Fetch on vehicleId prop change (fallback)
  useEffect(() => {
    if (vehicleId && !sessionStorage.getItem('currentVehicleId')) {
      sessionStorage.setItem('currentVehicleId', vehicleId);
      fetchChecklistHistory();
    }
  }, [vehicleId, fetchChecklistHistory]);

  /* --------------------------------------------------------------
     RENDER
     -------------------------------------------------------------- */
  return (
    <>
      <ToastContainer position="top-center" autoClose={4000} />

      <div
        className="modal fade"
        id="bs-checklist-history-list"
        tabIndex="-1"
        aria-labelledby="checklistHistoryLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="checklistHistoryLabel">
                Checklist History
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <div className="modal-body">
              {/* FILTERS */}
              <div className="filter-search mb-3">
                <div className="row g-2">
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={filterType}
                      onChange={handleFilterTypeChange}
                    >
                      <option value="all">All</option>
                      <option value="today">Today</option>
                      <option value="date">By Date</option>
                      <option value="name">By Name</option>
                    </select>
                  </div>

                  {filterType === 'date' && (
                    <div className="col-md-3">
                      <input
                        type="date"
                        className="form-control"
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                      />
                    </div>
                  )}

                  {filterType === 'name' && (
                    <div className="col-md-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter name"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="col-md-3">
                    <button className="btn btn-primary w-100" onClick={handleSearch}>
                      Search
                    </button>
                  </div>
                </div>
              </div>

              {/* TABLE */}
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No Checklist History Found
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table text-nowrap customize-table mb-0 align-middle">
                    <thead className="text-dark fs-4">
                      <tr>
                        <th><h6 className="fw-semibold mb-0">Checked Date</h6></th>
                        <th><h6 className="fw-semibold mb-0">Checked By</h6></th>
                        <th><h6 className="fw-semibold mb-0">Action</h6></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((item) => (
                        <tr key={item.id}>
                          <td>
                            {item.checked_date
                              ? new Date(item.checked_date).toLocaleString()
                              : 'N/A'}
                          </td>
                          <td>{item.checked_by}</td>
                          <td>
                            <button
                              onClick={() => handleViewDetails(item.id)}
                              className="h4 border-0 bg-transparent"
                              title="View Details"
                            >
                              <i className="ti ti-eye text-primary" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-danger"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChecklistHistoryModal;